import { useEffect, useMemo, useReducer } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { isEmbedMode, requestParentResize, scrollToTopForEmbed } from "../../util";

const FORTUNE_LAB_BASE_URL =
  process.env.REACT_APP_FORTUNE_LAB_BASE_URL || process.env.REACT_APP_BASE_URL || "";

interface TrackingInfoState {
  companyName: string | null;
  contactName: string | null;
  contactTel: string | null;
  contactEmail: string | null;
  useFallback: boolean;
}

type TrackingInfoAction =
  | { type: "set"; payload: TrackingInfoState }
  | { type: "error" }
  | { type: "reset" };

const DEFAULT_STATE: TrackingInfoState = {
  companyName: null,
  contactName: null,
  contactTel: null,
  contactEmail: null,
  useFallback: false,
};

const infoReducer = (state: TrackingInfoState, action: TrackingInfoAction): TrackingInfoState => {
  switch (action.type) {
    case "set":
      return action.payload;
    case "error":
    case "reset":
      return DEFAULT_STATE;
    default:
      return state;
  }
};

const FortuneLabPrivacyPolicyPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const trkId = useMemo(() => searchParams.get("trkId")?.trim() ?? "", [searchParams]);
  const embedMode = isEmbedMode();

  useEffect(() => {
    document.title = "오늘의 운세";
  }, []);

  const [state, dispatch] = useReducer(infoReducer, {
    ...DEFAULT_STATE,
    useFallback: trkId.length === 0,
  });

  useEffect(() => {
    if (!trkId || !FORTUNE_LAB_BASE_URL) {
      dispatch({
        type: "set",
        payload: {
          ...DEFAULT_STATE,
          useFallback: true,
        },
      });
      return undefined;
    }

    let isCancelled = false;

    const fetchInfo = async () => {
      try {
        const response = await fetch(
          `${FORTUNE_LAB_BASE_URL}/fortune-lab/tracking/info?trkId=${encodeURIComponent(trkId)}`
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (isCancelled) {
          return;
        }

        const info = data?.contents ?? {};
        const sanitized = (value: unknown) =>
          typeof value === "string" && value.trim().length > 0 ? value.trim() : null;

        dispatch({
          type: "set",
          payload: {
            companyName: sanitized(info?.companyName),
            contactName: sanitized(info?.ceoName),
            contactTel: sanitized(info?.contactTel),
            contactEmail: sanitized(info?.contactEmail),
            useFallback: false,
          },
        });
      } catch (error) {
        console.error("fortune-lab privacy policy info error", error);
        if (!isCancelled) {
          dispatch({ type: "error" });
        }
      }
    };

    fetchInfo();

    return () => {
      isCancelled = true;
    };
  }, [trkId]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
    if (embedMode) {
      scrollToTopForEmbed("auto");
    }
  }, [embedMode]);

  useEffect(() => {
    if (!embedMode) {
      return;
    }
    requestParentResize([0, 300, 600, 900]);
  }, [state, embedMode]);

  const resolvedCompanyName = state.useFallback
    ? state.companyName || "코어데브"
    : state.companyName || "";
  const resolvedContactName = state.useFallback
    ? state.contactName || "원현우"
    : state.contactName || "";
  const resolvedContactTel = state.useFallback
    ? state.contactTel || "010-4681-1565"
    : state.contactTel;
  const resolvedContactEmail = state.useFallback
    ? state.contactEmail || "sajutalktalk@gmail.com"
    : state.contactEmail;
  const contactInfo = [resolvedContactTel, resolvedContactEmail]
    .filter((value): value is string => Boolean(value))
    .join(" / ");

  const handleBack = () => {
    if (embedMode) {
      try {
        window.parent.postMessage(
          {
            type: "SAJU_PRIVACY_RETURN",
            source: "today-saju-privacy",
            trkId,
          },
          "*"
        );
      } catch (postMessageError) {
        console.warn("[FortuneLabPrivacyPolicyPage] postMessage failed", postMessageError);
      }
      // 스크롤 올리기
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      scrollToTopForEmbed("smooth");
    }

    const hasHistory = window.history.length > 1;
    if (hasHistory) {
      navigate(-1);
      return;
    }

    const fallbackSearch = searchParams.toString();
    navigate(`/fortune-lab${fallbackSearch ? `?${fallbackSearch}` : ""}`, { replace: true });
  };

  return (
    <div className="main_container">
      <main className="privacy_policy_main">
        <header className="privacy_policy_main__header">
          <button
            type="button"
            className="privacy_policy_main__back_button"
            onClick={handleBack}
            aria-label="뒤로가기"
          >
            ←
          </button>
          <h1>개인정보 처리방침</h1>
        </header>
        <div className="privacy_policy_main__content">
          <section>
            <h2>개인정보 수집·이용 동의서</h2>
            <p>
              <strong>{resolvedCompanyName}</strong> (이하 “회사”)는 고객님의 개인정보를 중요하게 생각하며 개인정보보호 관련
              법령을 준수합니다. 본 동의서는 개인정보 수집 및 이용과 관련된 내용을 안내하고, 이를 통해 고객님께 최적의
              서비스를 제공하고자 합니다.
            </p>
          </section>

          <section>
            <h3>1. 수집 항목 및 방법</h3>
            <h4>필수 수집 정보</h4>
            <ul>
              <li>이름</li>
              <li>전화번호</li>
              <li>생년월일 (사주 분석을 위한 정보)</li>
            </ul>
            <h4>자동 수집 정보</h4>
            <ul>
              <li>IP 주소</li>
              <li>쿠키</li>
              <li>접속 기록</li>
              <li>기기 정보</li>
            </ul>
          </section>

          <section>
            <h3>2. 수집 및 이용 목적</h3>
            <h4>서비스 제공 및 관리</h4>
            <ul>
              <li>맞춤형 사주 분석 결과 제공</li>
              <li>생년월일 및 관련 정보를 활용한 오늘의 운세 서비스 제공</li>
              <li>고객 요청 및 문의 사항 응대</li>
            </ul>
            <h4>마케팅 및 프로모션</h4>
            <ul>
              <li>맞춤형 이벤트 및 프로모션 안내</li>
              <li>관심사 기반 서비스 추천</li>
              <li>SMS 및 알림톡을 통한 광고·정보성 알림 발송</li>
              <li>쿠폰 발급 및 소멸 안내</li>
            </ul>
            <h4>통계 및 품질 개선</h4>
            <ul>
              <li>서비스 이용 기록 분석 및 콘텐츠 최적화</li>
              <li>신규 서비스 개발 및 기존 서비스 개선</li>
            </ul>
            <h4>법적 의무 준수</h4>
            <ul>
              <li>관련 법령에 따른 개인정보 보관 및 제공</li>
            </ul>
          </section>

          <section>
            <h3>3. 보유 및 이용 기간</h3>
            <p>수집 목적 달성 후 지체 없이 파기하며, 아래의 경우 예외적으로 보관합니다.</p>
            <ul>
              <li>서비스 이용 기록: 수집일로부터 1년</li>
              <li>계약 또는 청약철회 기록: 5년 (전자상거래법)</li>
              <li>소비자 불만 또는 분쟁 처리 기록: 3년 (전자상거래법)</li>
              <li>접속 기록: 6개월 (통신비밀보호법)</li>
            </ul>
          </section>

          <section>
            <h3>4. 제3자 제공</h3>
            <p>
              회사는 고객님의 동의 없이 개인정보를 제3자에게 제공하지 않습니다. 다만, 아래의 경우 예외적으로 제공합니다.
            </p>
            <ul>
              <li>제공받는 자: {resolvedCompanyName || "제휴사"}</li>
              <li>제공 목적: 사주 분석 및 운세 결과 발송</li>
              <li>제공 항목: 이름, 생년월일, 연락처</li>
              <li>보유 및 이용 기간: 분석 후 3년</li>
            </ul>
            <p>동의하지 않을 권리가 있으며, 동의 거부 시 일부 서비스 이용이 제한될 수 있습니다.</p>
          </section>

          <section>
            <h3>5. 개인정보 처리 위탁</h3>
            <p>회사는 원활한 서비스 제공을 위해 다음과 같이 개인정보 처리를 위탁할 수 있습니다.</p>
            <ul>
              <li>수탁업체: 비즈엠</li>
              <li>위탁업무: 카카오톡 알림톡 발송 및 쿠폰 관련 정보성 메시지 발송</li>
            </ul>
          </section>

          <section>
            <h3>6. 개인정보보호 책임자</h3>
            <ul>
              <li>이름: {resolvedContactName}</li>
              <li>연락처: {contactInfo || "제공된 연락처 없음"}</li>
            </ul>
          </section>

        <section>
          <h3>정책 변경</h3>
          <p>본 개인정보 처리방침은 법령 또는 회사 정책에 따라 변경될 수 있으며, 변경 사항은 사전에 공지합니다.</p>
        </section>
        {embedMode && (
          <button type="button" className="privacy_policy_main__cta" onClick={handleBack}>
            운세 보러가기
          </button>
        )}
      </div>
    </main>
  </div>
  );
};

export default FortuneLabPrivacyPolicyPage;
